import { SearchOutlined } from '@mui/icons-material';
import {
	CircularProgress,
	Grid,
	Typography,
	InputAdornment,
	styled,
	TextField,
	IconButton,
	Tooltip,
} from '@mui/material';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';

import { useCallback, useState, useEffect, useContext, useRef } from 'react';

import { Link } from 'react-router-dom';

import PageTitle from 'components/PageTitle/PageTitle';
import { FeatureFlags } from 'context/FeatureFlagWrapper';

import { ReactComponent as Downarrowchevron } from 'common/assets/images/downarrowchevron.svg';
import { ReactComponent as RefreshIcon } from 'common/assets/images/refresh.svg';
import Breadcrumbs from 'common/components/Breadcrumbs/Breadcrumbs';
import ButtonComp from 'common/components/Button/ButtonComp';
import MultipleSelectCheckmarks from 'common/components/MulltipleSelectCheckmarks/MultipleSelectCheckmarks';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { CLUSTER_RUN_STATUS, REVIEW_STATUS } from 'common/constants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import {
	APP_GENERAL_ERROR_MSG,
	ControlDeDuplicationText,
} from 'common/constants/TextContent';
import {
	dateLongFormat,
	dateFullYearFormat,
	urlWithTenant,
} from 'common/utils/utilFunctions';

import { useFeatureFlag } from 'hooks/useFeatureFlags';

import { getFeedingInventoryData } from '../api';

import ControlDeDuplicationTable from './ControlDeDuplicationTable';

import styles from './ControlDeDuplicationLandingPage.module.scss';

const { searchCompBgColor, searchCompBorderColor, checkboxSelectionColor } =
	styles;

const { INPROGRESS, COMPLETED } = CLUSTER_RUN_STATUS;

const ControlDeDuplicationLandingPage = () => {
	const featureFlagsContext = useContext(FeatureFlags);
	const [, setError] = useState<boolean>(false);
	const [, setLoading] = useState<boolean>(false);
	const [status, setStatus] = useState<string>('');
	const [lastExecutionEndTime, setLastExecutionEndTime] = useState<string>('');
	const anchorRef = useRef<HTMLDivElement>(null);
	const [toastClusterMessage, setToastClusterMessageLocal] = useState(
		'Control De-duplication processing in progress....Please refresh the table below for the latest results.',
	);
	const [isClusterMsgToastOpen, setIsClusterMsgToastOpen] = useState(false);
	const [searchVal, setSearchVal] = useState('');

	const StyledTextField = styled(TextField)(() => ({
		background: searchCompBgColor,
		borderRadius: '4px',
		border: `'1px solid ${searchCompBorderColor}'`,
		width: '60%',
	}));

	const handleClusterToast = () => {
		setIsClusterMsgToastOpen(false);
		setToastClusterMessageLocal('');
		setSearchVal('');
	};

	const setLoaderState = (callstate: boolean) => {
		if (callstate) {
			setLoading(true);
		}
	};

	const getRunClusterResponseData = async (initialcall: boolean) => {
		setError(false);
		setLoaderState(initialcall);
		const response = await getFeedingInventoryData();
		if (response) {
			if (response?.responseBody) {
				const responseData = response?.responseBody;
				if (responseData?.status === INPROGRESS) {
					setIsClusterMsgToastOpen(true);
					setToastClusterMessageLocal(
						'Control De-duplication processing in progress....Please refresh the table below for the latest results.',
					);
				}
				if (responseData?.status === COMPLETED) {
					setIsClusterMsgToastOpen(false);
					setToastClusterMessageLocal('');
				}
				setStatus(responseData?.status);
				setLastExecutionEndTime(responseData?.lastExecutionTime);
				setLoading(false);
			}
		} else {
			setError(true);
			setToastClusterMessageLocal(APP_GENERAL_ERROR_MSG);
			setLoading(false);
		}
	};
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const id = open ? 'simple-popper' : undefined;

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(anchorEl ? null : event.currentTarget);
	};

	const runCluster = useCallback(() => {
		// onlcick runcluster
	}, []);

	useEffect(() => {
		let intervalId: any;
		if (status === INPROGRESS) {
			intervalId = setInterval(() => {
				getRunClusterResponseData(false);
			}, 3000);
		}
		return () => {
			clearInterval(intervalId);
		};
	}, [status]);

	useEffect(() => {
		getRunClusterResponseData(true);
	}, []);

	ControlDeDuplicationText.body = (
		<div>
			{lastExecutionEndTime && (
				<div>
					Last Executed:
					<span className={styles['date-style']}>
						{lastExecutionEndTime
							? dateFullYearFormat(lastExecutionEndTime)
							: '--/--/--'}
					</span>
				</div>
			)}
		</div>
	);
	const breadcrumbs = [
		<Link key="1" to={urlWithTenant(`/${rrcpDashboardRoutes.aimodels}`)}>
			AI Models
		</Link>,
		<Typography key="2">Control De-duplication</Typography>,
	];

	const isControlDeDuplicationFilterEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_CONTROL_DEDUPLICATION_FILTER_ENABLED,
	);

	if (!featureFlagsContext) {
		// Handle the case where the context value is undefined
		return null; // or another fallback UI
	}

	return (
		<Grid container data-testid="control-dedup-landing-pageID">
			<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
				<AlertComponent
					open={isClusterMsgToastOpen}
					toastMessage={toastClusterMessage}
					zIndex={100}
					severity="info"
					handleCloseMessage={handleClusterToast}
					vertical="top"
					horizontal="center"
					variant="filled"
					icon={<CircularProgress color="inherit" size="2rem" />}
					className={styles['alert-comp-style']}
				/>
			</Grid>
			<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
				<Breadcrumbs breadcrumbs={breadcrumbs} />
				<PageTitle text={ControlDeDuplicationText} />
			</Grid>
			<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
				<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
					<ButtonComp
						btnType="primary"
						clickHandler={runCluster}
						disabled
						compClass={`${styles['control-dedup-page-btn']}`}
					>
						Run Cluster
					</ButtonComp>
				</Grid>
				<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
					<div className={`${styles['cluster-div-align']}`}>
						<Typography className={`${styles['cluster-title']}`}>
							Clusters
						</Typography>
					</div>
				</Grid>
				<Grid
					className={
						!lastExecutionEndTime ? styles['search-filter-div-style'] : ''
					}
				>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
						<Grid item xs={8} className={`${styles['cluster-div-align']}`}>
							<StyledTextField
								value={searchVal}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchOutlined className={styles['input-search-icon']} />
										</InputAdornment>
									),
								}}
								fullWidth
								id="fullWidth"
								placeholder="Search"
								data-testid="searchInput"
							/>
						</Grid>
					</Grid>
					<Grid
						container
						item
						xs={12}
						sm={12}
						md={12}
						lg={12}
						xl={12}
						className={
							lastExecutionEndTime
								? styles['control-dedup-table-header']
								: styles['filter-div-style-hidden']
						}
					>
						{lastExecutionEndTime && (
							<Grid
								item
								xs={6}
								sm={6}
								md={8}
								lg={8}
								xl={8}
								className={`${styles['table-message-header']}`}
							>
								<Typography className={`${styles['table-message']}`}>
									Displayed results as of {dateLongFormat(lastExecutionEndTime)}
									. kindly refresh for the most recent data.
								</Typography>
							</Grid>
						)}
						<Grid
							container
							item
							xs={6}
							sm={6}
							md={4}
							lg={4}
							xl={4}
							className={styles['btn-style']}
						>
							<Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
								<Tooltip title="Refresh Data">
									<IconButton
										aria-label="Refresh Data"
										className={styles['refresh-btn-style']}
									>
										<RefreshIcon fill={checkboxSelectionColor} />
										<Typography className={styles['refresh-data']}>
											Refresh Data
										</Typography>
									</IconButton>
								</Tooltip>
							</Grid>
							{isControlDeDuplicationFilterEnabled && (
								<Grid
									item
									xs={6}
									sm={6}
									md={6}
									lg={6}
									xl={6}
									className={`${styles['filter-btn-style']}`}
								>
									<ButtonGroup
										variant="contained"
										ref={anchorRef}
										aria-label="split button"
									>
										<Button className={`${styles['filter-btn']}`}>
											Filters
										</Button>
										<Button
											className={`${styles['filter-arrow-btn']}`}
											size="small"
											onClick={handleClick}
											data-testid="filter-icon-down"
										>
											<Downarrowchevron
												fill={searchCompBgColor}
												className={styles['downarrow-white']}
											/>
										</Button>
									</ButtonGroup>
									<Popper
										className={styles['popper-style']}
										id={id}
										open={open}
										anchorEl={anchorEl}
										placement="bottom-end"
										sx={{ height: '40vh', backgroundColor: searchCompBgColor }}
									>
										<Paper className={styles['paper-style']}>
											<MultipleSelectCheckmarks
												className={styles['dropdowns-select']}
												isClearFilter
												isSearchBox={false}
												optionsProps={REVIEW_STATUS}
												inputLabel="Review Status"
												onSelectedChange={() => {}}
												isInputLabel={false}
											/>
										</Paper>
									</Popper>
								</Grid>
							)}
						</Grid>
					</Grid>
				</Grid>
				<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
					<ControlDeDuplicationTable />
				</Grid>
			</Grid>
		</Grid>
	);
};

export default ControlDeDuplicationLandingPage;
